/**
 * Template VARIANTS derived from a template by data, never by copying it: a
 * versus variant seats a second player that mirrors the first (the same
 * kind's modules and configs under its own name) and decides the match with a
 * versus objective. Every tunable of the first seat's kind also writes the
 * second seat's copy (`mirrors`), so a patch sets each once, and the spec
 * validation's mirror rule keeps the match fair whatever a patch sets. The
 * second seat's art is DERIVED from the first's (`render.derivedFrom`): the
 * same accepted sprite and strips, recoloured to its own palette slot.
 */
import { cloneJson } from '../../json.js';
import type { ObjectiveConfig } from '../modules/flow.js';
import type { TemplateDisclosureV1, TemplateV1, TunableV1 } from './template.js';

export interface VersusVariantV1 {
  id: string;
  title: string;
  pitch: string;
  disclosure: TemplateDisclosureV1;
  /** The second seat: its kind name, role line and palette slot (it enters
   * at the first seat's spawn; its art is the first seat's, recoloured to it). */
  rival: { kind: string; role: string; palette: number };
  objective: Pick<ObjectiveConfig, 'win' | 'count'>;
  /** Required events of the match (a versus run between bots is shorter than
   * a solo clear, so it owes fewer of the base's obligations). */
  requiredEvents: string[];
  /** The modules the MATCH depends on (each stub-checked by its proof). */
  signatureModules: string[];
  /** Tunables of the variant's own objective. */
  objectiveTunables: TunableV1[];
}

/** A variant is derived at module load, so it runs wherever the catalog does,
 * including the private Tank sandbox, whose realm has no `structuredClone`. */
const clone = <T>(value: T): T => cloneJson(value) as unknown as T;

export function versusVariantOf(base: TemplateV1, v: VersusVariantV1): TemplateV1 {
  const spec = clone(base.spec);
  if (spec.players.archetypes.length !== 1) throw new RangeError(`${base.id} is not a one-seat template`);
  const seat = spec.players.archetypes[0]!;
  if (spec.archetypes[v.rival.kind]) throw new RangeError(`${base.id} already has a kind ${v.rival.kind}`);
  spec.archetypes[v.rival.kind] = { ...clone(spec.archetypes[seat]!), role: v.rival.role,
    render: { ...clone(spec.archetypes[seat]!.render), palette: v.rival.palette, derivedFrom: seat } };
  spec.players = { archetypes: [seat, v.rival.kind], spawns: [...spec.players.spawns, clone(spec.players.spawns[0]!)], relation: 'versus' };
  const objective = spec.globals.find(g => g.use === 'flow.objective');
  if (!objective) throw new RangeError(`${base.id} has no flow.objective`);
  objective.config = { ...objective.config, win: v.objective.win, count: v.objective.count };
  const seatPrefix = `/archetypes/${seat}/`;
  const objectivePath = '/globals/flow.objective/';
  const tunables = base.tunables.filter(t => !t.path.startsWith(objectivePath));
  return {
    ...clone(base), id: v.id, title: v.title, pitch: v.pitch, disclosure: clone(v.disclosure), spec,
    tunables: [...tunables.map(t => t.path.startsWith(seatPrefix)
      ? { ...t, mirrors: [`/archetypes/${v.rival.kind}/${t.path.slice(seatPrefix.length)}`] } : t), ...v.objectiveTunables],
    requirements: { events: [...v.requiredEvents] },
    signatureModules: [...v.signatureModules],
  };
}
